import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getTruthSummary, mapApiTruthSummaryToUi } from '../../../services/truth.js';

/**
 * The Truth counts of the Knowledge summary line (CT-5717 / CT-5719). Race
 * guard, memoized return.
 *
 * The counts are kept WITH the record they belong to: the Knowledge shell stays
 * mounted across records, so while the next record's counts load the line shows
 * none — never the previous record's (Bugbot, #570).
 */
export function useTruthSummary({ entityType, entityId } = {}) {
  const recordKey = `${entityType}/${entityId}`;
  const [loaded, setLoaded] = useState(null); // { recordKey, summary }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!entityType || !entityId) return;
    const requestId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const { body } = await getTruthSummary({ entityType, entityId });
      if (requestId !== fetchIdRef.current) return;
      setLoaded({ recordKey: `${entityType}/${entityId}`, summary: body ? mapApiTruthSummaryToUi(body) : null });
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      setLoaded(null);
      setError(err?.message ?? 'Failed to load the Truth summary');
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  const summary = loaded?.recordKey === recordKey ? loaded.summary : null;

  return useMemo(() => ({ summary, loading, error, refetch: fetchData }), [summary, loading, error, fetchData]);
}

export default useTruthSummary;
